import ButtonComponent from 'components/ButtonComponent';
import { buildResourceLink } from 'lib/communication';
import { generateTempLink } from 'lib/communication/files';
import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useHistory, useLocation } from 'react-router-dom';
import { sessionService } from 'services';

export enum UrlExpiredMessageTypes {
    OpenNewPreview = 'OPEN_NEW_PREVIEW',
    GeneratePreviewLink = 'GENERATE_PREVIEW_LINK'
}

export interface UrlExpiredMessage {
    type: string;
    content: string;
}

enum ModalType {
    ParentWindowClosed,
    TooManyAttempts,
    UnidentifiedError
}

const UrlExpired = () => {
    const history = useHistory();
    const location = useLocation();
    const [showModal, setShowModal] = useState<boolean>(false);
    const [modalType, setModalType] = useState<ModalType>();
    const parent: Window | null = window.opener;
    const queryParams = new URLSearchParams(location.search);
    const fileName = queryParams.get('fileName');
    const isMainWindow = sessionService.getToken() !== null;

    const setModal = (modal: ModalType) => {
        setModalType(modal);
        setShowModal(true);
    };

    const urlExpiredMessageHandler = useCallback(async (event: MessageEvent) => {
        event.preventDefault();
        event.stopPropagation();

        if (event.origin !== window.origin) {
            return;
        }

        const { type, content } = event.data as UrlExpiredMessage;
        switch (type) {
            case UrlExpiredMessageTypes.OpenNewPreview:
                if (content === 'User not authenticated or invalid token') {
                    setModal(ModalType.ParentWindowClosed);
                    return;
                }
                if (content === 'Too Many Attempts.') {
                    setModal(ModalType.TooManyAttempts);
                    return;
                }
                if (content === '') {
                    setModal(ModalType.UnidentifiedError);
                    return;
                }
                window.location.replace(buildResourceLink(content));
                break;
            default:
                break;
        }
    }, []);

    useEffect(() => {
        window.addEventListener('message', urlExpiredMessageHandler, false);

        return () => { window.removeEventListener('message', urlExpiredMessageHandler, false); };
    }, [urlExpiredMessageHandler]);

    if (fileName === null) {
        history.replace(process.env.PUBLIC_URL);
        return <></>;
    }

    const createNewPreview = async () => {
        if (parent !== null) {
            if (parent.closed) {
                setModal(ModalType.ParentWindowClosed);
                return;
            }
            parent.postMessage({ type: UrlExpiredMessageTypes.GeneratePreviewLink, content: fileName }, parent.origin);
        } else {
            if (!isMainWindow) {
                setModal(ModalType.ParentWindowClosed);
                return;
            }
            const url = await generateTempLink(fileName);
            if (url === '') return;
            window.location.replace(buildResourceLink(url));
        }
    };

    const modalHide = () => {
        setShowModal(false);
        switch (modalType) {
            case ModalType.ParentWindowClosed: {
                history.replace(`${process.env.PUBLIC_URL}/?fileName=${encodeURIComponent(fileName)}`);
                break;
            }
            default: {
                break;
            }
        }
    };

    return (
        <>
            <div className='App-body' style={{ display: 'flex', height: '100vh', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
                <h3>Link z podglądem stracił ważność. Proszę kliknąć poniższy przycisk, w celu wygenerowania nowego podglądu.</h3>
                <ButtonComponent text='Nowy podgląd' onHoverAnimation={1} variant='primary' onClick={createNewPreview} />
            </div>
            <Modal
                show={showModal}
                onHide={modalHide}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Wystąpił problem</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{ textAlign: 'center' }}>{
                        (() => {
                            switch (modalType) {
                                case ModalType.ParentWindowClosed: {
                                    return 'Sesja wygasła, zostaniesz przekierowany do logowania.';
                                }
                                case ModalType.TooManyAttempts: {
                                    return 'Zbyt wiele żądań, spróbuj ponownie za minutę.';
                                }
                                default: {
                                    return 'Wystąpił niezidentyfikowany błąd';
                                }
                            }
                        })()
                    }
                    </div>
                </Modal.Body>
                <Modal.Footer style={{ display: 'flex', justifyContent: 'center' }}>
                    <ButtonComponent margin='0' variant='primary' text='OK' onClick={() => { setShowModal(false); modalHide(); }} />
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default UrlExpired;
